import { useEffect, useState } from "react"
import { Card, Flex, Form, InputNumber, Switch, Tooltip } from "antd"
import { InfoCircleOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { useWizardContext } from "../../../WizardContext"
import styles from "./BasePrices.module.css"

const isPriceInvalid = (priceEntry, isEnabled) =>
  isEnabled && priceEntry.price !== null && priceEntry.price !== undefined && priceEntry.price <= 0

const BasePrices = () => {
  const { t } = useTranslation()
  const { form, trip } = useWizardContext()

  // Reactively tracks ticket_types as the user adds/removes them in TicketTypeStep.
  // Fall back to Redux trip data at render time when the form hasn't been initialized yet
  // (stale Redux cache race — WizardProvider initializes once and the guard blocks re-init).
  const watchedTypes = Form.useWatch("ticket_types", form)
  const ticketTypes = (watchedTypes?.length ? watchedTypes : trip?.ticket_types) || []

  const [ticketPrices, setTicketPrices] = useState(
    () => form.getFieldValue("ticketTypePrices") || {}
  )

  // Keep form in sync so buildPricingPayload can read ticketTypePrices
  useEffect(() => {
    form.setFieldValue("ticketTypePrices", ticketPrices)
  }, [ticketPrices, form])

  // Seed prices from Redux when form wasn't initialized with ticket_type prices (stale cache race).
  useEffect(() => {
    if (Object.keys(ticketPrices).length > 0 || !trip?.ticket_types?.length) return
    setTicketPrices(
      Object.fromEntries(
        trip.ticket_types.map((t) => [t.id, { enabled: t.enabled !== false, price: t.price > 0 ? t.price : null }])
      )
    )
  }, [trip?.ticket_types])

  const handleToggle = (id, checked) => {
    if (!checked) {
      const enabledCount = Object.values(ticketPrices).filter((p) => p.enabled !== false).length
      if (enabledCount <= 1) return
    }
    setTicketPrices((prev) => ({ ...prev, [id]: { ...prev[id], enabled: checked } }))
  }

  const handlePriceChange = (id, value) =>
    setTicketPrices((prev) => ({ ...prev, [id]: { ...prev[id], price: value } }))

  if (ticketTypes.length === 0) {
    return (
      <Card>
        <Text fw={600} size="xl">{t("experiences.pricing.base_prices.title")}</Text>
        <Flex align="center" gap={8} className="mt-m">
          <InfoCircleOutlined className={styles.infoIcon} />
          <Text fw={500}>{t("experiences.pricing.base_prices.no_ticket_types_hint")}</Text>
        </Flex>
      </Card>
    )
  }

  return (
    <Card>
      <Text fw={600} size="xl">{t("experiences.pricing.base_prices.title")}</Text>
      <Flex vertical gap={4} className="mt-m">
        {ticketTypes.map((ticket) => {
          const priceEntry = ticketPrices[ticket.id] || {}
          const isEnabled = priceEntry.enabled !== false

          const isInvalid = isPriceInvalid(priceEntry, isEnabled)

          return (
            <Flex key={ticket.id} vertical gap={2}>
              <Flex align="center" gap={8} className={styles.ticketRow}>
                <Flex align="center" gap={12} className={styles.ticketLeft}>
                  <Switch checked={isEnabled} onChange={(checked) => handleToggle(ticket.id, checked)} />
                  <Flex align="center" gap={8} flex={1}>
                    <Text className={styles.ticketLabel}>{ticket.title}</Text>
                    {ticket.description && (
                      <Tooltip title={ticket.description}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                      </Tooltip>
                    )}
                  </Flex>
                </Flex>
                <InputNumber
                  value={priceEntry.price ?? null}
                  onChange={(val) => handlePriceChange(ticket.id, val)}
                  status={isInvalid ? "error" : undefined}
                  prefix="$"
                  min={0}
                  precision={2}
                  placeholder="0.00"
                  className={styles.priceInput}
                  size="large"
                />
              </Flex>
              {isInvalid && (
                <Text size="s" className={styles.errorText}>{t("experiences.pricing.base_prices.price_must_be_positive")}</Text>
              )}
            </Flex>
          )
        })}
      </Flex>
    </Card>
  )
}

export default BasePrices
